import type {
  InstructionHandbookSectionConfig,
  InstructionHandbookSectionRole,
} from "./types";

/**
 * Section configurations for instruction handbook document generation (ISO 20607:2019).
 * Content-only sections; excludes form elements (title page, table of contents, index, annexes).
 * Task messages are derived from ISO 20607-2019_extracted.txt, including all subclauses.
 * How to use the product RAG tool is defined in the server instruction-handbook agent system prompt, not repeated here.
 */
export const INSTRUCTION_HANDBOOK_SECTION_CONFIGS: InstructionHandbookSectionConfig[] =
  [
    {
      section: "1",
      title: "Introduction / purpose of this instruction handbook",
      taskMessage: `Extract and write the introduction and purpose of this instruction handbook from the product source documents.

ISO 20607 5.2.1.1 (content parts c, d only): The instruction handbook shall include, as applicable:
c) introduction to/purpose of this instruction handbook;
d) how to read and apply the instruction handbook.

If the handbook consists of more than one part, include: addressed target groups; abilities/skills required by the target group(s); reader's guide/marking conventions/structure of the instruction handbook.
`,
    },
    {
      section: "2",
      title: "Safety",
      taskMessage: `Generate the Safety section for an ISO 20607-compliant instruction handbook from the product source documents.

ISO 20607 5.2.2.1 General: General safety requirements, not restricted to specific tasks, shall be provided together in a specific part of the instruction handbook, preferably in the "Safety" clause. For specific tasks described in the instruction handbook, safety information shall be included where the task is described.

5.2.2.2 General rules for preparation of the "Safety" clause: Include (a) warnings about potentially hazardous situations that can occur during intended use of the machine; (b) warnings regarding potentially hazardous situations arising from reasonably foreseeable misuse; (c) residual risks to be described; (d) general warnings and precautions to be included. The following topics shall be added, if relevant: substances and emissions that can create risk for persons involved in machine operations; warnings where the use of the machine can negatively influence electrically controlled medical devices (e.g. pacemakers); content related to the safety of user groups, with attention to protect vulnerable user groups (e.g. children, elderly people); personal protective equipment to be used for specific tasks; safety symbols, safety signs and/or pictograms used in and on the machine (including an image of them); location of safety symbols/signs/pictograms on the machine; instructions for renewing safety symbols/signs/pictograms on the machine, if necessary. Standardized symbols (e.g. ISO 7010, IEC 61310) shall be used to indicate important messages such as cautions, warnings and safety instructions.

5.2.2.3 Emergency situations: Where applicable, include (a) the operating method to be followed in the event of accident or breakdown; (b) the type of fire-fighting equipment to be used; (c) a warning of possible emission or leakage of hazardous substance(s) and, if applicable, an indication of means for fighting their effects.
`,
    },
    {
      section: "3",
      title: "Machine overview",
      taskMessage: `Generate the Machine overview section for an ISO 20607-compliant instruction handbook from the product source documents.

ISO 20607 5.2.3: The instruction handbook shall include a general description of the machine. The following information shall be included, where applicable: (a) machine description; (b) graphical description (e.g. floor plan, drawings, images); (c) intended use including the type of material/products that can be processed by the machine; (d) specific applications that are prohibited; (e) key machine specifications: length, width and height; mass; operating speed; characteristics of the power supply; limits for intended operating conditions (temperature, relative humidity, air pressure); intended life limit of the machine; lifetime (mission time) of components where relevant for safety aspects; (f) control and display descriptions; (g) requirements regarding (work)place or environment in which the machine is foreseen to be used (e.g. indoors, outdoors, clean room, potentially explosive atmosphere) (h) a description of the workstation(s) likely to be occupied by operators (i) the essential characteristics of tools which may be fitted to the machinery;.
`,
    },
    {
      section: "4",
      title: "Transportation, handling and storage",
      taskMessage: `Generate the Transportation, handling and storage section for an ISO 20607-compliant instruction handbook from the product source documents.

ISO 20607 5.2.4: The following information shall be included in the instruction handbook for transportation, handling and storage of the machine and/or the components, if relevant: physical characteristics such as dimensions during transportation, mass and centre of gravity of the machine, gripping/lifting points to be used; recommended transportation and handling method including required skills and equipment; environmental conditions for storage, such as limits for temperature, relative humidity and avoidance of direct sunlight.
`,
    },
    {
      section: "5",
      title: "Assembly, installation and commissioning",
      taskMessage: `Generate the Assembly, installation and commissioning section for an ISO 20607-compliant instruction handbook from the product source documents.

ISO 20607 5.2.5.1 Assembly of the machine: Where the machine is not assembled or installed by the manufacturer, include if relevant: requirements and procedures for assembly and mounting (e.g. temporary support or safeguarding, appropriate climatic conditions); requirements for testing where necessary; use of special tools and equipment; information on safe disposal of packaging materials and preservation measures.

5.2.5.2 Positioning of the machine: Where the machine is not positioned by the manufacturer, include if relevant: minimum requirements for placing (e.g. floor characteristics, point load, dynamic load, levelling, alignment); minimum requirements for fixing and anchoring the machine; requirements for accessibility around the machine (e.g. for maintenance, cleaning, sanitizing, work spaces).

5.2.5.3 Installation: Include information for mechanical, pneumatic, hydraulic and electrical installations, and in particular the required characteristics of the energy supply. If relevant: characteristics of necessary fluids including hazardous substances; necessity for an additional exhaust extraction system and its characteristics.

5.2.5.4 Checking and testing of safety systems: Include procedures for checking and testing safety systems. Where the installation check is not performed by the manufacturer, include procedures for checking the correct installation if relevant. Each safety-relevant check shall be described with: what to be checked; how to check; criteria for acceptance/non-acceptance; action required in case of non-acceptance.

5.2.5.5 Commissioning: Where the machine is not commissioned by the manufacturer, include procedures for commissioning and first use, such as: specific risks and hazards and precautions to be taken; information about special procedures for commissioning if required; securing and packaging materials that shall be removed (also consider protective films, strips, etc.); necessary tools and equipment; procedures such as adjustments, testing, measuring and reporting; and inspections.

5.2.5.6 Instructions relating to installation and assembly for reducing noise or vibration: Where the machine is not commissioned by the manufacturer, include procedures for reducing the noise and vibration from the machine.
`,
    },
    {
      section: "6",
      title: "Original equipment manufacturer settings",
      taskMessage: `Generate the Original equipment manufacturer settings section for an ISO 20607-compliant instruction handbook from the product source documents.

ISO 20607 5.2.6: The instruction handbook shall include instructions regarding original equipment manufacturer settings where accessible for the machine user, such as: mechanical settings and synchronization; safety-related (setting) parameters; pneumatic, hydraulic, electrical and vacuum settings; and other settings.
`,
    },
    {
      section: "7",
      title: "Operation",
      taskMessage: `Generate the Operation section for an ISO 20607-compliant instruction handbook from the product source documents.

ISO 20607 5.2.7: The instruction handbook shall include instructions regarding operating safety considerations, such as: intended use; residual risks; information regarding safety-relevant emissions; actuation of controls; setting and adjustment; operating modes; location of the intended operator positions; error messages from the control system and location of the error; personal protective equipment needed to be used; an indication of training requirements; checking of hazard area for the absence of persons; procedures for starting, control during operation, stopping, emergency situation, reset and restarting (including relevant safety-related parts); conversions or tool changes; procedures for clearing jams or blockages without creating hazards; information regarding the operational environment; information about vibration emission of hand-held machines and hand-guided machinery; illustrations that clarify key functions and risk reduction measures; recommendations for removal of waste; machine start-up and shut-down; sequence or chronology of operations; and other operating instructions if applicable. Operations that must be carried out by more than one person shall be prescribed. Appropriate warning shall be provided about the consequences if the recommended sequence of operations is not followed. The operator shall be informed of possible error messages and indications of warning components such as perceptible indication of normal and abnormal operation and recognition of signals. Signals or displays intended to indicate imminent risk of hazardous situations shall be stated in a comprehensible and unambiguous manner. Where machinery is likely to emit non-ionising radiation which may cause harm to persons, in particular persons with active or non-active implantable medical devices, information concerning the radiation emitted for the operator and exposed persons.
`,
    },
    {
      section: "8",
      title: "Product or capacity changeover",
      taskMessage: `Generate the Product or capacity changeover section for an ISO 20607-compliant instruction handbook from the product source documents.

ISO 20607 5.2.8: The instruction handbook shall include instructions and safety requirements on foreseeable product or capacity changeover, if applicable: product or workpiece size changes; tool changes; material changes; energy changes; equipment changes; data changes; process changeover; and procedures to restore operation. The instruction handbook shall include product-specific set-up information.
`,
    },
    {
      section: "9",
      title: "Inspection, testing and maintenance",
      taskMessage: `Generate the Inspection, testing and maintenance section for an ISO 20607-compliant instruction handbook from the product source documents.

ISO 20607 5.2.9: The instruction handbook shall include general maintenance instructions and safety-relevant information, such as: intended lifetime for the machine and its components; nature, frequency and criteria for inspections; specification of the spare parts, in particular those affecting the safety of the machine; characteristics of the substances to be used; instructions on how to safely perform maintenance operations (instructions relating to maintenance operations which require technical knowledge or particular skills and need to be carried out exclusively by skilled persons; instructions relating to maintenance actions which do not require specific skills and may be carried out by other persons); drawings and diagrams enabling maintenance personnel to carry out their task rationally; an overview of necessary tools, replaceable resources, and cleaning equipment needed for maintenance; energy control procedures (e.g. isolation, dissipation and/or restraint, marking and span-of-control); procedures to restore operation; replacement parts identification; a timetable for necessary maintenance with references on the appropriate procedures where applicable; any restrictions on maintenance that may be performed by the manufacturer because of safety reasons only. Specific risks and hazards during maintenance shall be taken into account. Information on the recommended spare parts to ensure a safe operation of the machine shall be given or referenced. All safety-relevant inspection, testing and maintenance for the whole machine and its accessories shall be described in one single section, with relevant information on: (a) what to be inspected/tested/maintained; (b) frequency of the inspections/tests/maintenance; (c) how to inspect/test/maintain; (d) criteria for acceptance or non-acceptance; (e) action required in case of non-acceptance.
`,
    },
    {
      section: "10",
      title: "Cleaning and sanitizing",
      taskMessage: `Generate the Cleaning and sanitizing section for an ISO 20607-compliant instruction handbook from the product source documents.

ISO 20607 5.2.10: The instruction handbook shall include instructions and information to clean and/or sanitize a machine to an appropriate level for the application, such as: tools, equipment and/or characteristics of cleaning agents required; personal protective equipment required; specific mode of operation; required energy isolation or control; cleaning and sanitizing procedures; and procedures to restore operation.
`,
    },
    {
      section: "11",
      title: "Fault finding / troubleshooting and repair",
      taskMessage: `Generate the Fault finding/troubleshooting and repair section for an ISO 20607-compliant instruction handbook from the product source documents.

ISO 20607 5.2.11: The instruction handbook shall include instructions and information for general fault finding/troubleshooting and repair, such as: fault identification and location for repair; troubleshooting; and procedures to repair the machine and restore operation. The fault list should be based on certain logic (e.g. first faults that are easy to resolve, or the most common faults first). Where necessary, add special notes about faults, precautionary measures and safety instructions. The instruction handbook shall include instructions for reporting specific or unexpected faults or failures to the manufacturer.
`,
    },
    {
      section: "12",
      title: "Dismantling, disabling and scrapping",
      taskMessage: `Generate the Dismantling, disabling and scrapping section for an ISO 20607-compliant instruction handbook from the product source documents.

ISO 20607 5.2.12: The instruction handbook shall include instructions for dismantling, disabling and scrapping of a machine, if applicable: preventive measures (technical and organizational); energy control procedures (e.g. isolation, dissipation and/or restraint); special tools and equipment; special risk reduction measures; required personal protective equipment; sequence or chronology for decommissioning. Scrapping instructions shall describe the actions the user has to perform at the end of the life cycle of a machine or part, with instructions for dismantling, disposal, recycling and/or destruction, with safety instructions and precautions against risks and hazards to health and the environment.
`,
    },
    {
      section: "13",
      title: "Documents and drawings",
      taskMessage: `Generate the Documents and drawings section for an ISO 20607-compliant instruction handbook from the product source documents.

ISO 20607 5.2.13: The instruction handbook shall include, or reference, necessary documents and drawings, such as: electrical, hydraulic, pneumatic schematics as appropriate; parts lists; components supplier instruction handbooks; and safety-relevant documents, drawings, data sheets and declarations.
`,
    },
  ];

/** Canonical section ids in handbook order (ISO 20607 content sections 5.2.1–5.2.13). */
export const INSTRUCTION_HANDBOOK_SECTION_IDS: string[] =
  INSTRUCTION_HANDBOOK_SECTION_CONFIGS.map((config) => config.section);

interface BaseSectionEntry {
  section: string;
  parentSection?: string;
  role?: "headerOnly";
}

const BASE_SECTION_ENTRIES: BaseSectionEntry[] =
  INSTRUCTION_HANDBOOK_SECTION_CONFIGS.map((config) => ({
    section: config.section,
  }));

function resolveBaseSectionRole(
  entry: BaseSectionEntry,
): InstructionHandbookSectionRole {
  if (entry.role === "headerOnly") {
    return "headerOnly";
  }
  if (entry.parentSection != null) {
    return "subsection";
  }
  return "content";
}

const INSTRUCTION_HANDBOOK_SECTION_ROLE_BY_ID = new Map<
  string,
  InstructionHandbookSectionRole
>(
  BASE_SECTION_ENTRIES.map((entry) => [
    entry.section,
    resolveBaseSectionRole(entry),
  ]),
);

/** Manifest role for a canonical instruction handbook section id. */
export function getInstructionHandbookSectionRole(
  sectionId: string,
): InstructionHandbookSectionRole {
  return INSTRUCTION_HANDBOOK_SECTION_ROLE_BY_ID.get(sectionId) ?? "content";
}

export function isInstructionHandbookHeaderOnlySection(
  sectionId: string,
): boolean {
  return getInstructionHandbookSectionRole(sectionId) === "headerOnly";
}

export function isInstructionHandbookSubsectionSection(
  sectionId: string,
): boolean {
  return getInstructionHandbookSectionRole(sectionId) === "subsection";
}

/**
 * When partial regen targets a headerOnly parent, include child section ids.
 * v1 manifest is flat 1–13; expansion is a no-op until parent/child entries exist.
 */
export function expandInstructionHandbookSectionsToGenerate(
  sectionsToGenerate: string[] | undefined,
): string[] | undefined {
  if (sectionsToGenerate == null) {
    return undefined;
  }

  const expanded = new Set(sectionsToGenerate);
  for (const entry of BASE_SECTION_ENTRIES) {
    if (entry.role === "headerOnly" && expanded.has(entry.section)) {
      for (const child of BASE_SECTION_ENTRIES) {
        if (child.parentSection === entry.section) {
          expanded.add(child.section);
        }
      }
    }
  }

  return INSTRUCTION_HANDBOOK_SECTION_IDS.filter((sectionId) =>
    expanded.has(sectionId),
  );
}

/**
 * Appendix for section 5 when the machine is partly completed.
 * ISO 20607 clauses 5.2.5.7–5.2.5.9.
 */
export const SECTION_5_PARTLY_COMPLETED_MACHINE_APPENDIX = `
5.2.5.7 Status of machinery control units under the Machinery Directive: where a control unit/board is an integral part of the Machine and delivers safety functions, give information specifically regarding the safe incorporation of the control unit/board.

5.2.5.8 Assembly instructions for interchangeable equipment: If relevant, specify the type or types of basic machinery with which the equipment can be safely used and include the necessary instructions for the safe assembly of the interchangeable equipment with the basic machinery by the user.

5.2.5.9 Machinery supplied without a drive system: In the case of machinery supplied without a drive system, the instructions must indicate all the necessary specifications for the drive system to be fitted such as the type, power and means of connection, and include precise fitting instructions for the drive system.`;

export function getPartiallyCompletedMachineSectionConfig(
  config: InstructionHandbookSectionConfig,
  isPartlyCompletedMachine: boolean | null | undefined,
): InstructionHandbookSectionConfig {
  if (config.section === "5" && isPartlyCompletedMachine === true) {
    return {
      ...config,
      taskMessage:
        config.taskMessage +
        "\n\n" +
        SECTION_5_PARTLY_COMPLETED_MACHINE_APPENDIX,
    };
  }
  return config;
}

/** Maps ISO handbook section number to stable extraction / agent registry key. */
const INSTRUCTION_HANDBOOK_EXTRACTION_SECTION_KEY_BY_SECTION: Record<
  string,
  string
> = {
  "1": "introductionPurpose",
  "2": "safety",
  "3": "machineOverview",
  "4": "transportationHandlingStorage",
  "5": "assemblyInstallationCommissioning",
  "6": "oemSettings",
  "7": "operation",
  "8": "productCapacityChangeover",
  "9": "inspectionTestingMaintenance",
  "10": "cleaningSanitizing",
  "11": "faultFindingRepair",
  "12": "dismantlingDisablingScrapping",
  "13": "documentsDrawings",
};

/**
 * Section list for information-for-use extraction prompts.
 * `sectionKey` values align with server `gatewayAgents.instructionHandbookSectionAgents` section ids.
 */
export const INSTRUCTION_HANDBOOK_SECTION_LIST_FOR_EXTRACTION: Array<{
  sectionKey: string;
  section: string;
  title: string;
}> = INSTRUCTION_HANDBOOK_SECTION_CONFIGS.map((config) => ({
  sectionKey:
    INSTRUCTION_HANDBOOK_EXTRACTION_SECTION_KEY_BY_SECTION[config.section],
  section: config.section,
  title: config.title,
}));
